/* Flavourful Bites cart drawer: AJAX Cart API + Section Rendering API.
   Public API: window.fbCart.add(items, { open, trigger }), .open(trigger), .close(), .refresh(). */
(function () {
  const SECTIONS = 'fb-cart-drawer,cart-icon-bubble';
  const root = () => (window.Shopify && Shopify.routes ? Shopify.routes.root : '/');
  const FOCUSABLE = 'a[href], button:not([disabled]), input:not([disabled]), textarea, select, [tabindex]:not([tabindex="-1"])';
  const escapeHtml = (s) => String(s).replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]));
  const CHECK = '<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.6" stroke-linecap="round" aria-hidden="true"><path d="M5 12l5 5 9-10"/></svg>';
  const CROSS = '<svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" aria-hidden="true"><path d="M6 6l12 12M18 6L6 18"/></svg>';

  class FbCartDrawer extends HTMLElement {
    connectedCallback() {
      this.panel = this.querySelector('.fb-cart__panel');
      this.busy = false;
      this.addEventListener('click', (e) => this.onClick(e));
      document.addEventListener('keydown', (e) => this.isOpen && this.onKey(e));
      window.fbCart = this;
    }

    get isOpen() {
      return !this.hidden;
    }

    open(trigger) {
      if (trigger) this.trigger = trigger;
      else if (!this.isOpen) this.trigger = document.activeElement;
      if (this.isOpen) return;
      clearTimeout(this.closeTimer);
      this.hidden = false;
      document.documentElement.classList.add('fb-cart-open');
      requestAnimationFrame(() => requestAnimationFrame(() => this.classList.add('is-open')));
      setTimeout(() => (this.querySelector('[data-cart-title]') || this.panel).focus(), 60);
    }

    close() {
      if (!this.isOpen) return;
      this.classList.remove('is-open');
      document.documentElement.classList.remove('fb-cart-open');
      this.closeTimer = setTimeout(() => {
        this.hidden = true;
        this.clearToasts(true);
      }, 300);
      if (this.trigger && document.contains(this.trigger)) this.trigger.focus();
    }

    onKey(e) {
      if (e.key === 'Escape') {
        e.preventDefault();
        this.close();
        return;
      }
      if (e.key !== 'Tab') return;
      const items = Array.from(this.panel.querySelectorAll(FOCUSABLE)).filter((el) => el.offsetParent !== null);
      if (!items.length) return;
      const first = items[0];
      const last = items[items.length - 1];
      if (e.shiftKey && (document.activeElement === first || !this.panel.contains(document.activeElement))) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault();
        first.focus();
      }
    }

    onClick(e) {
      if (e.target.closest('[data-cart-close]')) return this.close();
      const line = e.target.closest('[data-line]');
      if (line && e.target.closest('[data-inc]')) {
        const q = parseInt(line.dataset.qty, 10);
        if (q < 10) this.change(line, q + 1);
        return;
      }
      if (line && e.target.closest('[data-dec]')) {
        const q = parseInt(line.dataset.qty, 10);
        if (q > 1) this.change(line, q - 1);
        return;
      }
      if (line && e.target.closest('[data-remove]')) return this.remove(line);
      if (e.target.closest('[data-undo]')) return this.undo();
      if (e.target.closest('[data-dismiss]')) return this.clearToasts(true);
    }

    async request(url, body) {
      const res = await fetch(root() + url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify(Object.assign({ sections: SECTIONS, sections_url: window.location.pathname }, body)),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok || data.status) {
        const err = new Error(data.description || data.message || res.statusText || 'Something went wrong');
        err.data = data;
        throw err;
      }
      return data;
    }

    /* Re-fetch the drawer and header icon, e.g. when a response came back without section HTML. */
    async refresh() {
      try {
        const res = await fetch(root() + '?sections=' + SECTIONS, { headers: { Accept: 'application/json' } });
        if (res.ok) this.renderSections(await res.json());
      } catch (e) {}
    }

    async sync(data) {
      if (data && data.sections && data.sections['fb-cart-drawer'] && data.sections['cart-icon-bubble']) {
        this.renderSections(data.sections);
      } else {
        await this.refresh();
      }
    }

    /* Swap in the fresh drawer and header HTML returned by the Section Rendering API.
       The item count, the cart body and the footer (subtotal + Check out) are all updated, and the
       footer is shown or hidden to match, so nothing needs a page refresh. */
    renderSections(sections) {
      if (!sections) return;
      const drawerHtml = sections['fb-cart-drawer'];
      if (drawerHtml) {
        const doc = new DOMParser().parseFromString(drawerHtml, 'text/html');
        ['[data-cart-count]', '[data-cart-body]', '[data-cart-footer]'].forEach((sel) => {
          const fresh = doc.querySelector(sel);
          const current = this.querySelector(sel);
          if (!fresh) {
            if (current && sel === '[data-cart-footer]') current.hidden = true;
            return;
          }
          if (current) {
            current.innerHTML = fresh.innerHTML;
            current.hidden = fresh.hidden;
          } else if (sel === '[data-cart-footer]' && this.panel) {
            this.panel.appendChild(document.importNode(fresh, true));
          }
        });
      }
      const bubbleHtml = sections['cart-icon-bubble'];
      if (bubbleHtml) {
        const section = new DOMParser().parseFromString(bubbleHtml, 'text/html').querySelector('.shopify-section');
        if (section) document.querySelectorAll('#cart-icon-bubble').forEach((bubble) => (bubble.innerHTML = section.innerHTML));
      }
      this.labelHeader();
      if (this.isOpen && !this.panel.contains(document.activeElement)) {
        (this.querySelector('[data-cart-title]') || this.panel).focus();
      }
      document.dispatchEvent(new CustomEvent('fb:cart-updated'));
    }

    labelHeader() {
      const bubble = document.getElementById('cart-icon-bubble');
      const count = this.querySelector('[data-cart-count]');
      if (!bubble || !count) return;
      const n = parseInt(count.textContent.replace(/\D/g, ''), 10) || 0;
      bubble.setAttribute('aria-label', (this.dataset.openLabel || 'Open cart, {n} items').replace('{n}', n));
    }

    async add(items, opts = {}) {
      const data = await this.request('cart/add.js', { items });
      this.clearToasts(true);
      await this.sync(data);
      if (opts.open === false) return data;
      if (this.dataset.showToast === 'true') {
        const added = data.items || [];
        const n = added.reduce((s, it) => s + (it.quantity || 0), 0);
        const distinct = new Set(added.map((it) => it.product_id)).size;
        const text = distinct === 1 && added.length
          ? this.dataset.added.replace('{product}', added[0].product_title)
          : this.dataset.addedMulti.replace('{n}', n);
        this.toast('<div class="fb-cart__toast" role="status">' + CHECK + escapeHtml(text) + '</div>');
      }
      this.open(opts.trigger);
      return data;
    }

    async change(line, quantity) {
      line.classList.add('is-busy');
      try {
        const data = await this.request('cart/change.js', { id: line.dataset.key, quantity });
        this.clearToasts(false);
        await this.sync(data);
      } catch (e) {
        line.classList.remove('is-busy');
        this.lineError(line, e.message);
      }
    }

    async remove(line) {
      const removed = { id: parseInt(line.dataset.variant, 10), quantity: parseInt(line.dataset.qty, 10), title: line.dataset.title };
      line.classList.add('is-leaving');
      try {
        const [data] = await Promise.all([
          this.request('cart/change.js', { id: line.dataset.key, quantity: 0 }),
          new Promise((r) => setTimeout(r, 250)),
        ]);
        this.clearToasts(true);
        this.removed = removed;
        await this.sync(data);
        const text = (this.dataset.removed || 'Removed {product}').replace('{product}', '<strong>' + escapeHtml(removed.title) + '</strong>');
        this.toast(
          '<div class="fb-cart__removed" role="status"><span>' + text + '</span>' +
          '<button type="button" class="fb-cart__undo" data-undo>' + escapeHtml(this.dataset.undoLabel || 'Undo') + '</button>' +
          '<button type="button" class="fb-cart__dismiss" data-dismiss aria-label="Dismiss">' + CROSS + '</button></div>'
        );
      } catch (e) {
        line.classList.remove('is-leaving');
        this.lineError(line, e.message);
      }
    }

    async undo() {
      if (!this.removed) return;
      const { id, quantity } = this.removed;
      this.removed = null;
      try {
        const data = await this.request('cart/add.js', { items: [{ id, quantity }] });
        this.clearToasts(true);
        await this.sync(data);
      } catch (e) {
        this.error(e.message);
      }
    }

    toast(html) {
      const slot = this.querySelector('[data-toast-slot]');
      if (slot) slot.innerHTML = html;
    }

    clearToasts(all) {
      const slot = this.querySelector('[data-toast-slot]');
      if (slot && (all || !slot.querySelector('.fb-cart__removed'))) slot.innerHTML = '';
      if (all) this.removed = null;
      const err = this.querySelector('[data-cart-error]');
      if (err) err.hidden = true;
    }

    lineError(line, message) {
      const el = line.querySelector('[data-line-error]');
      if (!el) return this.error(message);
      el.hidden = false;
      el.textContent = message;
    }

    error(message) {
      const el = this.querySelector('[data-cart-error]');
      if (!el) return;
      el.hidden = false;
      el.textContent = message;
    }
  }

  customElements.get('fb-cart-drawer') || customElements.define('fb-cart-drawer', FbCartDrawer);

  const drawer = () => document.querySelector('fb-cart-drawer');

  /* Every product form posting to /cart/add opens the drawer instead of going to /cart: our own
     home page cards and flavour explorer, and Dawn's product forms (collection pages, quick add,
     featured product), whose own script would otherwise redirect to the cart page. Runs in the
     capture phase so it gets there before Dawn's handler. The product page buy box handles its own
     submit (and Buy now), so it's skipped. */
  document.addEventListener('submit', async (e) => {
    const form = e.target;
    const d = drawer();
    if (!d || !form.matches || !form.matches('form[action*="/cart/add"]')) return;
    if (form.hasAttribute('data-buy-form')) return;
    const submitter = e.submitter;
    if (submitter && submitter.name === 'return_to') return;
    e.preventDefault();
    e.stopImmediatePropagation();
    const fd = new FormData(form);
    const item = { id: parseInt(fd.get('id'), 10), quantity: parseInt(fd.get('quantity') || '1', 10) };
    if (!item.id) return;
    if (fd.get('selling_plan')) item.selling_plan = fd.get('selling_plan');
    for (const [key, value] of fd.entries()) {
      const m = key.match(/^properties\[(.+)\]$/);
      if (m && value !== '') (item.properties = item.properties || {})[m[1]] = value;
    }
    const btn = submitter || form.querySelector('[type="submit"]');
    if (btn) btn.disabled = true;
    try {
      await d.add([item], { trigger: btn || form });
      const modal = form.closest('quick-add-modal');
      if (modal && typeof modal.hide === 'function') modal.hide(true);
    } catch (err) {
      d.open(btn || form);
      d.error(err.message);
    } finally {
      if (btn) btn.disabled = false;
    }
  }, true);

  document.addEventListener('click', async (e) => {
    const d = drawer();
    if (!d) return;
    /* Header cart icon opens the drawer instead of navigating to /cart. */
    const icon = e.target.closest('#cart-icon-bubble');
    if (icon && !e.metaKey && !e.ctrlKey) {
      e.preventDefault();
      d.clearToasts(true);
      d.open(icon);
      return;
    }
    /* Buttons carrying their own line items, e.g. drawer upsells: data-items='[{"id":1,"quantity":1}]'. */
    const btn = e.target.closest('[data-add-to-cart][data-items]');
    if (!btn) return;
    e.preventDefault();
    btn.disabled = true;
    try {
      await d.add(JSON.parse(btn.dataset.items), { trigger: btn });
    } catch (err) {
      d.open(btn);
      d.error(err.message);
    } finally {
      if (document.contains(btn)) btn.disabled = false;
    }
  });

  /* Pages restored from the browser's back/forward cache keep an old cart; refresh it. */
  window.addEventListener('pageshow', (e) => {
    if (e.persisted && drawer()) drawer().refresh();
  });

  document.addEventListener('DOMContentLoaded', () => drawer() && drawer().labelHeader());
})();
